import { useEffect, useState } from 'react';
import { engineFetchJson } from '@/lib/engineHttp';
import type { WidgetDefinition, WidgetProps } from '@/types/widget';

interface AgentSession {
  id: string;
  status: string;
  agent_type?: string | null;
  token_usage?: {
    input_tokens?: number;
    output_tokens?: number;
  } | null;
  created_at?: string;
  updated_at?: string;
}

interface RecentAgentsResponse {
  sessions: AgentSession[];
  total: number;
}

interface TaskItem {
  id: string;
  title: string;
  status: string;
  assigned_to?: string | null;
  updated_at?: string;
}

const STATUS_COLORS: Record<string, string> = {
  todo: 'var(--sp-text-muted)',
  in_progress: 'var(--sp-accent)',
  blocked: 'var(--sp-error)',
  review: 'var(--sp-warn)',
  done: 'var(--sp-ok)',
  cancelled: 'var(--sp-text-dim)',
};

function timeAgo(dateStr: string | undefined): string {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
}

function RecentAgentActivityWidget({ context }: WidgetProps) {
  const [sessions, setSessions] = useState<AgentSession[] | null>(null);
  const [tasks, setTasks] = useState<TaskItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [usingFallback, setUsingFallback] = useState(false);

  useEffect(() => {
    void engineFetchJson<RecentAgentsResponse>('/agents/recent?limit=10')
      .then((data) => {
        setSessions(data.sessions ?? []);
        setTasks(null);
        setUsingFallback(false);
        setError(null);
      })
      .catch(() => {
        void engineFetchJson<TaskItem[]>('/tasks/?status=in_progress&limit=10')
          .then((items) => {
            items.sort((a, b) => {
              const da = a.updated_at ? new Date(a.updated_at).getTime() : 0;
              const db = b.updated_at ? new Date(b.updated_at).getTime() : 0;
              return db - da;
            });
            setTasks(items.slice(0, 10));
            setSessions(null);
            setUsingFallback(true);
            setError(null);
          })
          .catch((e) => {
            setError(e.message);
          });
      });
  }, [context.authToken]);

  return (
    <div className="sp-widget">
      <div className="sp-widget-header">
        <span>Recent Agent Activity</span>
        <a href="/board" style={{ fontSize: 11, fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>
          Full board
        </a>
      </div>
      <div className="sp-widget-body" style={{ padding: '2px 0' }}>
        {error ? (
          <div style={{ padding: '4px 12px' }}>
            <div style={{ color: 'var(--sp-warn)', fontSize: 12, marginBottom: 4 }}>
              Agent activity is not available right now
            </div>
            <div className="mono" style={{ color: 'var(--sp-text-dim)', fontSize: 11, marginBottom: 2 }}>
              GET /api/v1/agents/recent
            </div>
            <div className="mono" style={{ color: 'var(--sp-text-dim)', fontSize: 11 }}>
              Fallback: GET /api/v1/tasks/?status=in_progress
            </div>
            {/* Mock layout */}
            <div style={{ marginTop: 12, opacity: 0.4 }}>
              {['Auth-unify migration', 'Codex graph wiring', 'Governor canon eval'].map((t) => (
                <div key={t} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0', fontSize: 12 }}>
                  <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--sp-accent)' }} />
                  <span style={{ color: 'var(--sp-text-muted)' }}>{t}</span>
                  <span className="mono" style={{ fontSize: 10, color: 'var(--sp-text-dim)', marginLeft: 'auto' }}>2h</span>
                </div>
              ))}
            </div>
          </div>
        ) : sessions ? (
          sessions.length === 0 ? (
            <div style={{ padding: '8px 12px', color: 'var(--sp-text-muted)', fontSize: 12 }}>
              No recent agent sessions.
            </div>
          ) : (
            <div style={{ maxHeight: 240, overflow: 'auto' }}>
              {sessions.map((session) => (
                <div
                  key={session.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '5px 12px',
                    fontSize: 12,
                    borderBottom: '1px solid var(--sp-border)',
                  }}
                >
                  <div
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      background: STATUS_COLORS[session.status] ?? 'var(--sp-text-dim)',
                      flexShrink: 0,
                    }}
                    title={session.status}
                  />
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {session.agent_type || 'Harness session'}
                  </span>
                  {typeof session.token_usage?.input_tokens === 'number' ? (
                    <span className="mono" style={{ fontSize: 10, color: 'var(--sp-text-dim)' }}>
                      {session.token_usage.input_tokens + (session.token_usage.output_tokens ?? 0)} tok
                    </span>
                  ) : null}
                  <span className="mono" style={{ fontSize: 10, color: 'var(--sp-text-dim)', flexShrink: 0 }}>
                    {timeAgo(session.updated_at || session.created_at)}
                  </span>
                </div>
              ))}
            </div>
          )
        ) : tasks ? (
          tasks.length === 0 ? (
            <div style={{ padding: '8px 12px', color: 'var(--sp-text-muted)', fontSize: 12 }}>
              No in-progress tasks.
            </div>
          ) : (
            <div style={{ maxHeight: 240, overflow: 'auto' }}>
              {tasks.map((t) => (
                <div
                  key={t.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '5px 12px',
                    fontSize: 12,
                    borderBottom: '1px solid var(--sp-border)',
                  }}
                >
                  <div
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      background: STATUS_COLORS[t.status] ?? 'var(--sp-text-dim)',
                      flexShrink: 0,
                    }}
                    title={t.status}
                  />
                  <span
                    style={{
                      flex: 1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    title={t.title}
                  >
                    {t.title}
                  </span>
                  {t.assigned_to && (
                    <span className="mono" style={{ fontSize: 10, color: 'var(--sp-text-dim)' }}>
                      {t.assigned_to}
                    </span>
                  )}
                  <span className="mono" style={{ fontSize: 10, color: 'var(--sp-text-dim)', flexShrink: 0 }}>
                    {timeAgo(t.updated_at)}
                  </span>
                </div>
              ))}
            </div>
          )
        ) : (
          <div style={{ padding: '4px 12px', color: 'var(--sp-text-muted)', fontSize: 12 }}>Loading activity...</div>
        )}
        <div style={{ padding: '4px 12px', fontSize: 10, color: 'var(--sp-text-dim)', borderTop: '1px solid var(--sp-border)' }}>
          {usingFallback
            ? 'Showing in-progress tasks as a fallback because the agent feed was unavailable.'
            : 'Showing the live /agents/recent feed from the engine.'}
        </div>
      </div>
    </div>
  );
}

export const recentAgentActivityWidget: WidgetDefinition = {
  id: 'recent-agent-activity',
  name: 'Recent Agent Activity',
  description: 'Last 10 agent dispatches and active tasks.',
  icon: 'bot',
  category: 'agents',
  scope: 'both',
  defaultSize: { w: 6, h: 5 },
  minSize: { w: 4, h: 3 },
  component: RecentAgentActivityWidget,
};
